import { motion, AnimatePresence } from "framer-motion";
import { useModal } from "../../context/GameContext.jsx";
import { useGame } from "../../context/GameContext.jsx";
import { ROLE_DATA } from "../../data/roles.js";
import { scaleIn } from "../../animations/variants.js";

export function PlayerCardModal({ player, onClose }) {
  const { state } = useGame();
  const lang = state.language;
  const t = (fr, en) => lang === "fr" ? fr : en;
  const role = ROLE_DATA[player.baseRoleId];
  if (!role) return null;

  const isInfected    = player.statuses.includes("infected");
  const isRogueWC     = player.baseRoleId === "wild_child" && player.effectiveAlignment === "werewolf";
  const isLover       = player.statuses.includes("lover");
  const isMayor       = player.statuses.includes("mayor");
  const hasRaven      = player.statuses.includes("raven_mark");
  const isDead        = !player.alive;
  const hasSheep      = (player.sheepCount || 0) > 0;
  const elderHasLife  = player.baseRoleId === "elder" && player.alive && !state.session?.elder_usedSurvival;
  const elderLostLife = player.baseRoleId === "elder" && player.alive && state.session?.elder_usedSurvival;

  const alignLabel = player.effectiveAlignment === "village" ? t("Village","Village")
    : player.effectiveAlignment === "werewolf" ? t("Loup-Garou","Werewolf")
    : t("Neutre","Neutral");

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
        className="fixed inset-0 z-[9999] flex items-center justify-center px-6"
        style={{ background:"rgba(0,0,0,0.85)" }}
        onClick={onClose}>
        <motion.div
          initial={{ opacity:0, y:60, rotateY:-15, scale:0.85 }}
          animate={{ opacity:1, y:0, rotateY:0, scale:1,
            transition:{ type:"spring", stiffness:260, damping:22 } }}
          exit={{ opacity:0, y:40, scale:0.9 }}
          onClick={e => e.stopPropagation()}
          className={`w-full max-w-xs relative rounded-3xl border-2 ${role.border}
            bg-gradient-to-b ${role.gradient} overflow-hidden shadow-2xl`}>

          <button onClick={onClose}
            className="absolute top-3 right-3 z-20 w-8 h-8 rounded-full bg-black/60
              text-white/90 flex items-center justify-center text-sm active:scale-95 border border-white/10">✕</button>

          {isDead && (
            <div className="absolute inset-0 z-10 bg-black/60 flex items-center justify-center rounded-3xl">
              <p className="text-white/50 text-4xl rotate-12">💀</p>
            </div>
          )}

          <div className="w-full aspect-[3/4] bg-black/30 flex items-center justify-center relative">
            {role.imageUrl
              ? <img src={role.imageUrl} alt={role.englishName} className="w-full h-full object-cover" />
              : <span className="text-8xl select-none">{role.emoji}</span>}
            <span className={`absolute bottom-2 right-2 text-[10px] font-bold px-2 py-0.5
              rounded-full ${role.badge} text-white/90 border border-white/10`}>
              {alignLabel}
            </span>
          </div>

          <div className="px-4 pt-3 pb-4">
            <p className="text-white/40 text-[10px] uppercase tracking-widest">{player.name}</p>
            <h3 className="text-white font-bold text-lg leading-tight">
              {lang === "fr" ? role.frenchName : role.englishName}
            </h3>
            <p className="text-white/60 text-xs mt-1 leading-relaxed">
              {lang === "fr" ? role.frenchDescription : role.englishDescription}
            </p>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {isMayor && <span className="text-[10px] bg-yellow-900/60 border border-yellow-700/40 rounded-full px-2 py-0.5 text-yellow-300">👑 {t("Maire","Mayor")}</span>}
              {isInfected && <span className="text-[10px] bg-purple-950 border border-purple-700 rounded-full px-2 py-0.5 text-purple-300">🩸 {t("Infecté","Infected")}</span>}
              {isRogueWC && <span className="text-[10px] bg-red-950 border border-red-800 rounded-full px-2 py-0.5 text-red-300">🐺 {t("Loup Renégat","Rogue Werewolf")}</span>}
              {isLover && <span className="text-[10px] bg-pink-950 border border-pink-700 rounded-full px-2 py-0.5 text-pink-300">💕 {t("Amoureux","Lover")}</span>}
              {hasRaven && <span className="text-[10px] bg-slate-800 border border-slate-600 rounded-full px-2 py-0.5 text-slate-300">🦅 +2</span>}
              {hasSheep && <span className="text-[10px] bg-stone-800 border border-stone-600 rounded-full px-2 py-0.5 text-stone-300">+{player.sheepCount}🐑</span>}
              {elderHasLife && <span className="text-[10px] bg-yellow-900/60 border border-yellow-700/40 rounded-full px-2 py-0.5 text-yellow-300">+1❤️</span>}
              {elderLostLife && <span className="text-[10px] bg-stone-900 border border-stone-700 rounded-full px-2 py-0.5 text-stone-500">0💔</span>}
              {isDead && <span className="text-[10px] bg-stone-900 border border-stone-700 rounded-full px-2 py-0.5 text-stone-400">💀 {t("Éliminé","Eliminated")}</span>}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export function ClickablePlayer({ player, className = "" }) {
  const { setModalPlayer } = useModal();
  if (!player) return null;
  return (
    <button
      onClick={(e) => { e.stopPropagation(); setModalPlayer(player); }}
      className={`font-bold text-amber-300 underline decoration-dotted
        underline-offset-2 active:scale-95 transition-all ${className}`}>
      {player.name}
    </button>
  );
}
